<template>
  <banner-alert>
    <router-view v-if="!hideRouterView" />
    <IdentityVerification
      v-if="shouldRenderIDV"
      :prevent-redirects="true"
    />
    <PreferredNameModal />
    <ConfirmCompanyEmailModal />
    <ToastContainer />
  </banner-alert>
</template>
<script>
import { Client } from 'persona';
import dayjs from 'dayjs';
import BannerAlert from '@/components/BannerAlert.vue';
import { detectIncognito } from 'detectincognitojs';
import PreferredNameModal from '@/components/modals/PreferredNameModal.vue';
import ConfirmCompanyEmailModal from '@/components/modals/ConfirmCompanyEmailModal.vue';
import IdentityVerification from '@/views/signup/IdentityVerification.vue';
import { ToastContainer } from '@/components';

export default {
  name: 'App',
  components: {
    BannerAlert,
    IdentityVerification,
    PreferredNameModal,
    ConfirmCompanyEmailModal,
    ToastContainer,
  },
  computed: {
    /**
     * This computed was created for a specific edge case:
     * When users claim a carrier using a census line, then log out without completing IDV,
     * and later log back in. In this scenario, the IDV logic treats them as existing users
     * because they already have an associated company. However, these users are technically
     * new and lack an email address.
     *
     * Since these users automatically get access with `require_identity_proofing`,
     * the global IDV logic will run and mount the flow. This component receives the
     * `prevent-redirects` prop, which activates the modals as if the user were in the
     * onboarded IDV flow.
     *
     * To avoid this, the computed ensures that IDV is only triggered outside of the
     * sign-up flow, since those routes have their own dedicated view for handling IDV.
     *
     * More context: https://highwayorg.slack.com/archives/C07V1PMEGHE/p1748374457034069
     */
    shouldRenderIDV() {
      const routeName = this.$route?.name;
      const excludedRoutes = [
        'IdentityVerification',
        'SignUpOrLoginVerification',
      ];

      const value =
        this.showIDWFlow && routeName && !excludedRoutes.includes(routeName);

      return value;
    },
    hideRouterView() {
      // Early return if feature flag is disabled
      if (!this.enableHideRouterView) {
        return false;
      }

      //We want to hide the RouterView for any part of the Onboarding app for IDV, which is made up of three steps:
      // 	1.	Inquiry
      // 	2.	Preferred Name
      // 	3.	Email Verification and Email confirmation

      // This is to prevent malicious actors from using inspection tools to remove nodes from the DOM and still use the website.
      const {
        isPreferredNameModalActive,
        isCompanyEmailModalActive,
        confirmationPending,
      } = this.$store.state.identityVerification;

      if (
        isPreferredNameModalActive ||
        isCompanyEmailModalActive ||
        confirmationPending ||
        this.shouldRenderIDV
      ) {
        return true;
      }
      return false;
    },
    showIDWFlow() {
      const user = this.$store.state.users.currentUser;
      const latestInquiry = this.$store.getters['users/getLatestInquiry'];

      const { showIDVFlow } = this.$store.state.identityVerification;

      if (user?.last_verified_identity_at) return false;

      if (
        latestInquiry?.status === 'approved' ||
        latestInquiry?.status === 'declined' ||
        latestInquiry?.status === 'failed' ||
        latestInquiry?.status === 'under_review'
      )
        return false;

      if (this.isCompanyEnforced) return true;

      const status = user?.identity_proofing_status;

      if (status === 'skip_identity_proofing') return false;
      if (status === 'require_identity_proofing') return true;

      return showIDVFlow || false;
    },
    isCompanyEnforced() {
      if (!this.enableCompanyEnforceIdv) return false;

      return this.$store.state.carriers.carrierData['idv_enforced?'];
    },
    enableCompanyEnforceIdv() {
      return this.$store.state.users.featureFlags?.enable_company_enforced_idv
        ?.enabled;
    },
    enableHideRouterView() {
      return this.$store.state.users.featureFlags
        ?.enable_hide_router_view_for_idv?.enabled;
    },
  },
  watch: {
    '$store.state.users.currentUser': function (newVal) {
      if (newVal?.id > 0) {
        if (this.$store.state.users.intercomIsUserLoggedIn === false) {
          this.bootOrUpdateIntercomWithUser(newVal);
        }
        if (this.$rollbar?.client?.options?.payload) {
          let co = newVal?.company?.legal_name?.replace(/ /g, '-');
          let username = null;
          if (co?.length > 0) {
            username = co + '===' + newVal?.company?.id + '__' + newVal?.id;
          } else {
            username = 'None__' + newVal?.id;
          }
          this.$rollbar.client.options.payload['person'] = {
            id: newVal?.id,
            username: username,
          };
        }
      } else {
        if (this.$store.state.users.intercomIsBooted === false) {
          this.bootIntercom();
        }
      }
    },
  },
  created() {
    this.redirectToBlockPageIfNeeded();
  },
  mounted() {
    Client?.preload();
  },
  beforeUnmount() {
    this.shutdownIntercom();
  },
  methods: {
    async bootIntercom() {
      if (process.env.ENABLE_INTERCOM != 1) {
        return;
      }
      await window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: process.env.INTERCOM_APP_ID,
        hide_default_launcher: true,
        custom_launcher_selector: '#launch_highway_intercom',
      });
      this.$store.commit('users/setIntercomIsBooted', true);
    },
    async bootOrUpdateIntercomWithUser(currentUser) {
      if (process.env.ENABLE_INTERCOM != 1) {
        return;
      }
      if (this.$store.state.users.intercomIsBooted === false) {
        if (currentUser?.id > 0) {
          await window.Intercom('boot', {
            api_base: 'https://api-iam.intercom.io',
            app_id: process.env.INTERCOM_APP_ID,
            hide_default_launcher: true,
            custom_launcher_selector: '#launch_highway_intercom',
            user_id: currentUser?.id,
            user_hash: currentUser?.intercom_user_verification_hash,
            name: `${currentUser?.first_name} ${currentUser?.last_name}`,
            email: currentUser?.email, // the email for your user
            created_at: dayjs(currentUser?.created_at)?.unix(),
            phone: currentUser?.phone_e164,
            company: {
              id: currentUser?.company?.id,
              name: currentUser?.company?.legal_name,
              created_at: dayjs(currentUser?.company?.created_at)?.unix(),
              plan_id: currentUser?.company?.plan_id,
              company_status: currentUser?.company?.status,
            },
          });
          this.$store.commit('users/setIntercomIsUserLoggedIn', true);
        }
      } else {
        if (currentUser?.id > 0) {
          await window.Intercom('update', {
            user_id: currentUser?.id,
            user_hash: currentUser?.intercom_user_verification_hash,
            name: `${currentUser?.first_name} ${currentUser?.last_name}`,
            email: currentUser?.email,
            created_at: dayjs(currentUser?.created_at)?.unix(),
            phone: currentUser?.phone_e164,
            company: {
              id: currentUser?.company?.id,
              name: currentUser?.company?.legal_name,
              created_at: dayjs(currentUser?.company?.created_at)?.unix(),
              plan_id: currentUser?.company?.plan_id,
              company_status: currentUser?.company?.status,
            },
          });
          this.$store.commit('users/setIntercomIsUserLoggedIn', true);
        }
      }
      this.$store.commit('users/setIntercomIsBooted', true);
    },
    shutdownIntercom() {
      if (process.env.ENABLE_INTERCOM != 1) {
        return;
      }
      window.Intercom('shutdown');
      this.$store.commit('users/setIntercomIsBooted', false);
      this.$store.commit('users/setIntercomIsUserLoggedIn', false);
    },
    redirectToBlockPageIfNeeded() {
      if (window.location.href.includes('private-mode-block')) {
        return;
      }
      detectIncognito()
        .then((result) => {
          if (result.browserName === 'Safari' && result.isPrivate) {
            window.location.replace(
              window.location.origin + '/onboarding/private-mode-block'
            );
          }
        })
        .catch((error) => {
          console.error('Error detecting incognito mode: ', error);
        });
    },
  },
};
</script>
<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

p {
  font-size: 14px;
}

input:not([type='submit']):focus,
textarea:focus {
  /* @apply focus:outline-input; */
  @apply border border-body-color outline-none;
}

input[type='submit']:focus {
  @apply outline-none;
}
</style>
